import {
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Row,
  Select,
  Typography,
} from "antd";

import "./ProjectInformation.scss";
import { useLookupOptions, useAuditorSelectOptions } from "admin/queries";
import { ProjectFormFieldItem } from "../project-form-field-item/ProjectFormFieldItem";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { useDisplayFieldByStage } from "projects/hooks";
import { tagRender } from "shared/components";
import { DateFormat } from "shared/constants";

type Props = {
  form: FormInstance;
  currentStage: string;
  isEditing: boolean;
};

const fieldCategory = "projectInformationFields";

export const ProjectInformation = ({
  form,
  currentStage,
  isEditing,
}: Props) => {
  const validationStage = Form.useWatch("stage", form);
  const hasStageChanged = form.isFieldTouched("stage");
  const { displayFieldCategoryByStage } = useDisplayFieldByStage();
  const selectPlaceholder = isEditing ? "Select..." : "None";
  const datePlaceholder = isEditing ? "Select date" : "None";

  // Fetch auditor options from API
  const { data: auditorSelectOptions = [] } = useAuditorSelectOptions();

  // Fetch lookup options from API
  const { data: projectTypeOptions = [] } = useLookupOptions("project_type");
  const { data: utilityProgramOptions = [] } = useLookupOptions("utility_program");
  const { data: paymentMethodOptions = [] } = useLookupOptions("customer_payment_method");

  return displayFieldCategoryByStage(fieldCategory, currentStage) ? (
    <div className='project-information-container'>
      <Flex>
        <Typography.Title className='project-information-title' level={2}>
          Project Information
        </Typography.Title>
      </Flex>
      <Divider className='project-information-divider' />
      <Row justify='start' gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <ProjectFormFieldItem
          name='projectTypes'
          label='Project Type(s)'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <Select
            size='small'
            mode='multiple'
            tagRender={tagRender}
            options={projectTypeOptions}
            className='multi-select-box'
            placeholder={selectPlaceholder}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='utilityProgram'
          label='Utility Program'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <Select
            size='small'
            options={[{ value: "", label: selectPlaceholder }, ...utilityProgramOptions]}
            onSelect={(value) => {
              form.setFieldValue("utilityProgram", value);
            }}
            placeholder={selectPlaceholder}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='utilityProjectId'
          label='Utility Project ID'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <Input size='small' />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='customerPaymentMethod'
          label='Customer Payment Method'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <Select
            size='small'
            options={[{ value: "", label: selectPlaceholder }, ...paymentMethodOptions]}
            onSelect={(value) => {
              form.setFieldValue("customerPaymentMethod", value);
            }}
            placeholder={selectPlaceholder}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='ownerOrTenantPayee'
          label='Owner or Tenant Payee'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <Select
            size='small'
            options={[
              { value: "", label: selectPlaceholder },
              { value: "Owner", label: "Owner" },
              { value: "Tenant Payee", label: "Tenant Payee" },
            ]}
            onSelect={(value) => {
              form.setFieldValue("ownerOrTenantPayee", value);
            }}
            placeholder={selectPlaceholder}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='utilityBillsReceivedAndReviewed'
          label='Utility Bills Received and Reviewed'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "utilityBillsReceivedAndReviewed",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='auditRequestFormReceivedReviewed'
          label='Audit Request Form Received/Reviewed'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "auditRequestFormReceivedReviewed",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='auditorId'
          label='Auditor'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <Select
            size='small'
            options={[{ value: "", label: selectPlaceholder }, ...auditorSelectOptions]}
            onSelect={(value) => {
              form.setFieldValue("auditorId", value);
            }}
            placeholder={selectPlaceholder}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='auditScheduleDate'
          label='Audit Schedule Date'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "auditScheduleDate",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='onsiteAuditDone'
          label='Onsite Audit Done'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "onsiteAuditDone",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='ceToolDone'
          label='CE Tool Done'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "ceToolDone",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='hvacAssessmentFormDone'
          label='HVAC Assessment Form Done'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "hvacAssessmentFormDone",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='hvacPrePhotosReceived'
          label='HVAC Pre-photos Received'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "hvacPrePhotosReceived",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='sentToHcnForQuote'
          label='Sent to HCN For Quote'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "sentToHcnForQuote",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='hcnQuotePackageReceived'
          label='HCN Quote Package Received'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "hcnQuotePackageReceived",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='hvacQuoteSubsCertsApproved'
          label='HVAC Quote/Subs/Certs Approved'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "hvacQuoteSubsCertsApproved",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='proposalSentToSalesRep'
          label='Proposal Sent To Sales Rep'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "proposalSentToSalesRep",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='estimatedSaleDate'
          label='Esitmated Sale Date'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "estimatedSaleDate",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='soldQuarter'
          label='Sold Quarter'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <InputNumber size='small' />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='soldMonth'
          label='Sold Month'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <InputNumber size='small' />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='soldYear'
          label='Sold Year'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <InputNumber size='small' />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='reviewedSignedContractDocuments'
          label='Reviewed Signed Contract Documents'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "reviewedSignedContractDocuments",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='submittedToUtilityForApproval'
          label='Submitted to Utility for Approval'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "submittedToUtilityForApproval",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='submittedForFinancingApproval'
          label='Submitted for Financial Approval'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "submittedForFinancingApproval",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='financingApproved'
          label='Financing Approved'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "financingApproved",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='utilityPreInspectionDate'
          label='Utility Pre-Inspection Date'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "utilityPreInspectionDate",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='utilityPreInspectionDone'
          label='Utility Pre-Inspection Done'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "utilityPreInspectionDone",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
        <ProjectFormFieldItem
          name='utilityApprovalLetter'
          label='Utility Approval Letter'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
          getValueProps={(i) => ({
            value: isEmpty(i) ? null : dayjs(i, DateFormat),
          })}
        >
          <DatePicker
            allowClear
            size='small'
            placeholder={datePlaceholder}
            onChange={(x) => {
              form.setFieldValue(
                "utilityApprovalLetter",
                isEmpty(x) ? null : dayjs(x, DateFormat)
              );
            }}
            format={DateFormat}
          />
        </ProjectFormFieldItem>
      </Row>
    </div>
  ) : null;
};
